import React from 'react'
import { Link } from 'react-router-dom'
import { getMenuItem, getMenuLabel } from '../lib/menu/menuManifest'
import { isMenuItemVisible } from '../lib/menu/menuVisibility'
import MenuIcon from './MenuIcon'
import NavIcon from './NavIcon'

const VARIANT_CLASS = {
    dropdown: {
        link: 'flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-slate-50',
        panel: 'absolute left-0 top-[36px] w-52 rounded-lg border bg-white shadow-lg py-1',
        panelWide: 'absolute left-0 top-[36px] w-56 rounded-lg border bg-white shadow-lg py-1',
        divider: 'my-1 border-t',
    },
    compact: {
        link: 'flex items-center gap-2 px-5 py-1.5 text-xs text-slate-700 hover:bg-slate-50',
        panel: '',
        panelWide: '',
        divider: 'mx-2 my-1 border-t border-slate-100',
    },
    mobile: {
        link: 'flex items-center gap-2 hover:text-brand-sky',
        panel: 'mt-2 flex flex-col gap-2 pl-3 border-l',
        panelWide: 'mt-2 flex flex-col gap-2 pl-3 border-l',
        divider: 'my-1 border-t',
    },
    mobileCompact: {
        link: 'flex items-center gap-2 pl-2 text-xs text-slate-700 hover:text-brand-sky',
        panel: '',
        panelWide: '',
        divider: 'my-1 border-t',
    },
    ops: {
        link: 'flex items-center gap-2 text-sm hover:text-brand-sky',
        title: 'text-xs font-semibold text-slate-500 mb-1',
        group: 'pl-2 border-l space-y-1',
        section: 'px-3 py-2',
        panel: 'absolute left-0 top-[36px] w-56 rounded-lg border bg-white shadow-lg py-1',
    },
}

const VARIANT_ICON_SIZE = {
    dropdown: 18,
    compact: 16,
    mobile: 18,
    mobileCompact: 16,
    ops: 16,
}

function visibleItemsInGroup(group, ctx, audience) {
    return (group.itemIds || [])
        .map((id) => getMenuItem(id))
        .filter((item) => item && isMenuItemVisible(item, ctx, { audience }))
}

function shouldShowDividerBefore(group, ctx, audience) {
    if (!group.dividerBefore) return false
    const checkIds = group.dividerWhenAnyVisible || group.itemIds || []
    return checkIds.some((id) => isMenuItemVisible(getMenuItem(id), ctx, { audience }))
}

export function sectionHasVisibleItems(section, ctx) {
    const audience = section.audience || 'user-dropdown'
    return (section.groups || []).some((group) => visibleItemsInGroup(group, ctx, audience).length > 0)
}

export function simpleSectionHasVisibleItems(itemIds, ctx, audience = 'user-dropdown') {
    return (itemIds || []).some((id) => isMenuItemVisible(getMenuItem(id), ctx, { audience }))
}

export function ShellMenuGroupedPanel({
    groups,
    ctx,
    audience = 'user-dropdown',
    variant = 'dropdown',
    onItemClick,
    labelVariant = 'default',
    panelClassName,
}) {
    const classes = VARIANT_CLASS[variant] || VARIANT_CLASS.dropdown
    const nodes = []

    ;(groups || []).forEach((group, index) => {
        const items = visibleItemsInGroup(group, ctx, audience)
        if (!items.length) return

        if (shouldShowDividerBefore(group, ctx, audience)) {
            nodes.push(<div key={`div-before-${index}`} className={classes.divider} />)
        }

        items.forEach((item) => {
            const iconSize = VARIANT_ICON_SIZE[variant] || 18
            nodes.push(
                <Link
                    key={item.id}
                    className={[classes.link, item.shellLinkClass].filter(Boolean).join(' ')}
                    to={item.path}
                    onClick={onItemClick}
                >
                    <MenuIcon menuItemId={item.id} emoji={item.icon} className="text-slate-500" size={iconSize} />
                    <span className="min-w-0 truncate">{getMenuLabel(item, labelVariant)}</span>
                </Link>,
            )
        })

        if (group.trailingDividerWhenVisible) {
            nodes.push(<div key={`div-after-${index}`} className={classes.divider} />)
        }
    })

    if (!nodes.length) return null
    if (panelClassName || classes.panel) {
        return <div className={panelClassName || classes.panel}>{nodes}</div>
    }
    return <>{nodes}</>
}

export function ShellOpsMenuPanel({ groups, ctx, onItemClick }) {
    const classes = VARIANT_CLASS.ops
    return (
        <div className={classes.panel}>
            {(groups || []).map((group) => {
                const items = visibleItemsInGroup(group, ctx, 'user-dropdown')
                if (!items.length) return null
                return (
                    <div key={group.title} className={classes.section}>
                        <div className={classes.title}>{group.title}</div>
                        <div className={classes.group}>
                            {items.map((item) => (
                            <Link
                                key={item.id}
                                className={[classes.link, item.shellLinkClass].filter(Boolean).join(' ')}
                                to={item.path}
                                onClick={onItemClick}
                            >
                                <MenuIcon menuItemId={item.id} emoji={item.icon} className="text-slate-500" size={VARIANT_ICON_SIZE.ops} />
                                <span className="min-w-0 truncate">{getMenuLabel(item)}</span>
                            </Link>
                            ))}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

export function ShellNavDropdown({
    section,
    ctx,
    isOpen,
    onToggle,
    onClose,
    menuRef,
    variant = 'dropdown',
    labelVariant = 'default',
}) {
    if (!ctx[section.visibilityKey]) return null
    if (!sectionHasVisibleItems(section, ctx)) return null

    const classes = VARIANT_CLASS[variant] || VARIANT_CLASS.dropdown
    return (
        <div ref={menuRef} className="relative">
            <button
                type="button"
                className="inline-flex items-center gap-1.5 hover:text-brand-sky"
                aria-haspopup="menu"
                aria-expanded={isOpen}
                onClick={onToggle}
            >
                <NavIcon navItemId={section.id} size={18} />
                <span>{section.label}</span>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.24 4.5a.75.75 0 0 1-1.08 0l-4.24-4.5a.75.75 0 0 1 .02-1.06Z" clipRule="evenodd" />
                </svg>
            </button>
            {isOpen && (
                <ShellMenuGroupedPanel
                    groups={section.groups}
                    ctx={ctx}
                    audience={section.audience || 'user-dropdown'}
                    variant={variant}
                    onItemClick={onClose}
                    labelVariant={labelVariant}
                    panelClassName={section.id === 'admin' ? classes.panelWide : classes.panel}
                />
            )}
        </div>
    )
}

export function ShellSimpleDropdown({
    label,
    visibilityKey,
    ctx,
    itemIds,
    isOpen,
    onToggle,
    onClose,
    menuRef,
    panelClassName,
}) {
    if (!ctx[visibilityKey]) return null
    if (!simpleSectionHasVisibleItems(itemIds, ctx)) return null

    const panel = (
        <ShellMenuGroupedPanel
            groups={[{ itemIds }]}
            ctx={ctx}
            audience="user-dropdown"
            variant="dropdown"
            onItemClick={onClose}
            panelClassName={panelClassName || VARIANT_CLASS.dropdown.panel}
        />
    )
    if (!panel) return null

    return (
        <div ref={menuRef} className="relative">
            <button
                type="button"
                className="inline-flex items-center gap-1 hover:text-brand-sky"
                aria-haspopup="menu"
                aria-expanded={isOpen}
                onClick={onToggle}
            >
                {label}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.24 4.5a.75.75 0 0 1-1.08 0l-4.24-4.5a.75.75 0 0 1 .02-1.06Z" clipRule="evenodd" />
                </svg>
            </button>
            {isOpen && panel}
        </div>
    )
}

export function ShellMobileSection({ section, ctx, onClose, labelVariant = 'default' }) {
    if (!ctx[section.visibilityKey]) return null
    if (!sectionHasVisibleItems(section, ctx)) return null
    const panel = (
        <ShellMenuGroupedPanel
            groups={section.groups}
            ctx={ctx}
            audience={section.audience || 'user-dropdown'}
            variant="mobile"
            onItemClick={onClose}
            labelVariant={labelVariant}
            panelClassName={VARIANT_CLASS.mobile.panel}
        />
    )
    if (!panel) return null
    return (
        <div className="pt-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                <NavIcon navItemId={section.id} size={16} />
                <span>{section.label}</span>
            </div>
            {panel}
        </div>
    )
}

export function ShellOperatorUserMenuSections({
    sections,
    ctx,
    sectionsOpen,
    onToggleSection,
    onClose,
    variant = 'compact',
}) {
    return sections.map((section, index) => (
        <React.Fragment key={section.id}>
            {index > 0 && <div className="my-1 border-t" />}
            <button
                type="button"
                className="w-full px-3 py-1 text-xs font-semibold text-slate-500 flex items-center justify-between hover:bg-slate-50"
                onClick={() => onToggleSection(section.sectionKey || section.id)}
            >
                <span className="flex min-w-0 items-center gap-2">
                    <NavIcon navItemId={section.id} size={16} />
                    <span className="truncate">{section.label}</span>
                </span>
                <span>{sectionsOpen[section.sectionKey || section.id] ? '▾' : '▸'}</span>
            </button>
            {sectionsOpen[section.sectionKey || section.id] && (
                <ShellMenuGroupedPanel
                    groups={section.groups}
                    ctx={ctx}
                    audience={section.audience || 'operator-user-menu'}
                    variant={variant}
                    onItemClick={onClose}
                />
            )}
        </React.Fragment>
    ))
}

export function ShellOperatorUserMenuDropdown({
    ctx,
    sections,
    isOpen,
    onToggle,
    onClose,
    menuRef,
    sectionsOpen,
    onToggleSection,
}) {
    return (
        <div ref={menuRef} className="relative">
            <button
                type="button"
                className="inline-flex items-center gap-1 hover:text-brand-sky"
                aria-haspopup="menu"
                aria-expanded={isOpen}
                onClick={onToggle}
            >
                사용자 메뉴
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.24 4.5a.75.75 0 0 1-1.08 0l-4.24-4.5a.75.75 0 0 1 .02-1.06Z" clipRule="evenodd" />
                </svg>
            </button>
            {isOpen && (
                <div className="absolute left-0 top-[36px] w-64 rounded-lg border bg-white shadow-lg py-2">
                    <ShellOperatorUserMenuSections
                        sections={sections}
                        ctx={ctx}
                        sectionsOpen={sectionsOpen}
                        onToggleSection={onToggleSection}
                        onClose={onClose}
                    />
                </div>
            )}
        </div>
    )
}

export function ShellOpsNavDropdown({ ctx, isOpen, onToggle, onClose, menuRef, groups }) {
    if (!ctx.showOpsMgmtMenu) return null
    return (
        <div ref={menuRef} className="relative">
            <button
                type="button"
                className="inline-flex items-center gap-1 hover:text-brand-sky"
                aria-haspopup="menu"
                aria-expanded={isOpen}
                onClick={onToggle}
            >
                운영관리
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.24 4.5a.75.75 0 0 1-1.08 0l-4.24-4.5a.75.75 0 0 1 .02-1.06Z" clipRule="evenodd" />
                </svg>
            </button>
            {isOpen && <ShellOpsMenuPanel groups={groups} ctx={ctx} onItemClick={onClose} />}
        </div>
    )
}
